// src/shared/components/ui/ProductCombobox/ProductCombobox.tsx
// Componente especializado de combobox para seleccionar productos con imagen, nombre y código
'use client';

import { faChevronDown, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/shadcn/popover';
import { cn } from '@shared/utils/utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Product } from '../Product';
import type { ProductComboboxProps, ProductOption } from './types';

// 🎯 Hook para debounce personalizado
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const ProductCombobox = ({
  value,
  setValue,
  placeholder,
  searchFn,
  className = 'w-full',
  disabled = false,
  dataTestId = 'product-combobox',
  debounceMs = 100,
  pageSize = 200,
  emptyMessage = 'No se encontraron productos',
  loadingMessage = 'Buscando productos...',
  onOptionSelected,
}: ProductComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 🎯 Estado para mantener el producto seleccionado
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(
    null
  );

  // Referencias
  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce del query de búsqueda
  const debouncedSearchQuery = useDebounce(searchQuery, debounceMs);

  // 🎯 Configuración del virtualizador
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56, // Altura del componente Product
    overscan: 10,
  });

  // 🎯 Función de búsqueda
  const performSearch = useCallback(
    async (query: string) => {
      try {
        setIsLoading(true);
        const results = await searchFn(query);
        setOptions(results.slice(0, pageSize));

        // Si tenemos un valor seleccionado pero no está en selectedProduct, buscarlo
        if (value && !selectedProduct) {
          const foundProduct = results.find(
            (option) => option.value === value
          );
          if (foundProduct) {
            setSelectedProduct(foundProduct);
          }
        }
      } catch (err) {
        console.error('Error en búsqueda de productos:', err);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [searchFn, pageSize, value, selectedProduct]
  );

  // 🎯 Función para buscar el producto inicial (definir ANTES de usarla)
  const performInitialSearch = useCallback(
    async (searchValue: string) => {
      try {
        const results = await searchFn('');
        const foundProduct = results.find(
          (option) => option.value === searchValue
        );
        if (foundProduct) {
          setSelectedProduct(foundProduct);
        }
      } catch (err) {
        console.error('Error buscando producto inicial:', err);
      }
    },
    [searchFn]
  );

  // 🎯 Efecto para realizar búsqueda cuando cambie el query debounced
  useEffect(() => {
    if (open) {
      performSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, open, performSearch]);

  // 🎯 Efecto para mantener sincronizado el producto seleccionado
  useEffect(() => {
    if (!value) {
      setSelectedProduct(null);
    } else if (value && selectedProduct?.value !== value) {
      const foundProduct = options.find((option) => option.value === value);
      if (foundProduct) {
        setSelectedProduct(foundProduct);
      } else if (!selectedProduct) {
        performInitialSearch(value);
      }
    }
  }, [value, selectedProduct, options, performInitialSearch]);

  // 🎯 Manejar selección de producto
  const handleSelect = useCallback(
    (selectedValue: string) => {
      const product =
        options.find((opt) => opt.value === selectedValue) || null;
      if (product) {
        setSelectedProduct(product);
      }
      setValue(selectedValue);
      onOptionSelected?.(product);
      setOpen(false);
      setSearchQuery('');
    },
    [setValue, options, onOptionSelected]
  );

  // 🎯 Manejar apertura del popover
  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setSearchQuery('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  // 🎯 Manejar cambio en el input de búsqueda
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  // 🎯 Manejar teclas especiales
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  return (
    <Popover open={open && !disabled} onOpenChange={handleOpenChange}>
      {/* Trigger del desplegable */}
      <PopoverTrigger asChild>
        <div
          className={cn(
            className,
            'bg-card-bg border border-card-border rounded px-2 py-[9px] min-h-[42px] flex justify-between items-center cursor-pointer transition-colors',
            'hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            disabled && 'pointer-events-none opacity-70'
          )}
          data-testid={dataTestId}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
        >
          {/* Mostrar solo el nombre del producto seleccionado o placeholder */}
          {selectedProduct ? (
            <span className="text-foreground text-sm truncate">
              {selectedProduct.name}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          )}

          <FontAwesomeIcon
            icon={faChevronDown}
            className="ml-2 h-2 w-2 opacity-50 text-muted-foreground flex-shrink-0"
          />
        </div>
      </PopoverTrigger>

      {/* Contenido del desplegable */}
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-h-[400px] p-0 bg-card-bg text-popover-foreground border border-border">
        <div className="flex flex-col h-full max-h-80">
          {/* 🔍 Input de búsqueda */}
          <div className="flex items-center border-b border-border px-3 py-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar por nombre o código..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              onKeyDown={handleKeyDown}
              data-testid={`${dataTestId}-search-input`}
            />
            {isLoading && (
              <FontAwesomeIcon
                icon={faSpinner}
                className="ml-2 h-4 w-4 animate-spin text-muted-foreground"
              />
            )}
          </div>

          {/* 📜 Lista virtualizada de productos */}
          <div
            ref={parentRef}
            className="flex-1 overflow-auto"
            style={{ height: '280px' }}
            onWheel={(e) => {
              // 🎯 Detener propagación del scroll para que no afecte al modal/página
              e.stopPropagation();
            }}
          >
            {/* 🔄 Estado de loading */}
            {isLoading && options.length === 0 && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="mr-2 h-4 w-4 animate-spin"
                />
                {loadingMessage}
              </div>
            )}

            {/* 🕳️ Estado vacío */}
            {!isLoading && options.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            )}

            {/* 📋 Lista de productos virtualizados */}
            {options.length > 0 && (
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const product = options[virtualItem.index];
                  const isSelected = product.value === value;

                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      className={cn(
                        'absolute top-0 left-0 w-full px-3 py-2 cursor-pointer transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent/50'
                      )}
                      style={{
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      onClick={() => handleSelect(product.value)}
                      data-testid={`${dataTestId}-option-${product.id}`}
                    >
                      {/* Usar el componente Product para cada opción */}
                      <Product
                        id={product.id}
                        name={product.name}
                        code={product.code}
                        src={product.image || ''}
                        enableLineClamp={true}
                        showTooltip={false}
                        showImageModal={false}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProductCombobox;

